import React, { useContext, useEffect, useState, Suspense, useMemo, useCallback } from 'react';
import { navigate } from 'hooks/useHookRouterCompat';
import styled from 'styled-components';
import Product, * as Prod from 'data/Product';
import Vendor from 'data/Vendor';
import Attribute from 'data/Attribute';
import { ProductErrorType, validateProduct } from 'data/ProductValidation';
import PDPPreview from 'common/PDPPreview';
import Button from 'components/UI/Button';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { Color, Percent } from 'lib/styles';
import { getStash } from 'lib/stash';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { createAttributes, getAttributesPerBusinessUnit } from 'requests/atribute';
import { getVendorAttributes } from 'requests/vendor';
import { Background } from 'styles/themeColors';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import Variant, { VariantAttributeValue } from '../data/Variant';
import { useCurrencies } from 'hooks/useCurrencies';
import { PRODUCT_CONSTANTS } from 'constants/productColumns';
import { SocketContext } from 'context/socket';
import UploadProductVariant from 'components/Product/ProductVariantImportModal';
import {
  getColumnSettings,
  getProductById,
  getVariantsByProductId,
  loadProductDetails,
  updateColumnSettings,
} from 'requests/product';
import { STATUS } from 'constants/productVariantImport';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import TabsBarComponent from 'components/UI/Tabsbar';
import {
  ROUTES,
  TEXT_LABELS,
  attributeErrorMessage,
  productDetailsTab,
} from 'constants/productDetails';
import SubmitModal from 'components/Product/VariantListing/SubmitModal';
import HeaderButtonComponent from 'components/Product/Common/HeaderButtonComponent';
import { useCountries } from 'hooks/useCountries';
import ProductEditorV2 from 'common/ProductEditorV2';
import ProductBadge from 'data/ProductBadge';
import { loadProductBadges } from 'requests/ProductBadges';
import UserConfiguration from 'data/UserConfiguration';
import { compareWarehouseIds, PRODUCT_TYPE_CONFIG } from 'utils/product';
import KeepOrDiscardModal from 'components/Product/VariantListing/KeepModal';
import Warehouse from 'data/Warehouse';
import { PRODUCT_STATUS, PRODUCT_TYPE, TAB_KEYS, TabKey } from 'constants/product';
import { USER_CONFIG_TYPES } from 'constants/userConfiguration';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { BusinessUnit } from 'data/BusinessUnit';
import { GENERAL_CONSTANTS } from 'constants/general';
import SmartVariantsView from 'components/Product/SmartVariants/SmartVariantsView';
import AffiliateFormView, {
  AffiliateFormState,
  hasAffiliateFormError
} from 'components/Product/AffiliateForm/AffiliateFormView';
import { grantAccess } from 'utils/authorization';
const VariantTableV2 = React.lazy(() => import('common/VariantTableV2'));

const MemoizedVariantTableV2 = React.memo(VariantTableV2);

// Tabs that operate on a persisted product and so can't be opened while the details form
// still has unsaved required fields.
const SAVE_REQUIRED_TAB_KEYS: (TabKey | undefined)[] = [
  TAB_KEYS.VARIANTS,
  TAB_KEYS.WAREHOUSES,
  TAB_KEYS.SMART_VARIANTS,
  TAB_KEYS.AFFILIATE_FORM
];

const Page = styled.div<any>`
  -webkit-overflow-scrolling: touch;
  background-color: ${Background.white};
  color: ${Color.black};
  height: ${Percent.full};
  margin-left: 10px;
  width: calc(${Percent.full} - 10px);
  max-width: ${({ width }) => width ? `${width}px` : 'none'};
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0 30px;
  box-sizing: border-box;
  overflow-x: hidden;
`;
export interface Props {
  productId?: string;
  view?: 'details' | 'preview' | 'variants' | 'newProduct' | 'warehouses' | 'smart-variants' | 'affiliate-form';
  selectedVendor: Vendor;
  vendorsList?: Vendor[];
  updateVendor: (name: string, brandId?: number) => void;
  featureFlags: FeatureFlag;
  selectedBusinessUnit: BusinessUnit;
  productType?: string;
}

interface Pagination {
  actual: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRecords: number;
}

export interface Sorting {
  header: string;
  direction: string;
  sortField?: string;
  priceType?: string;
}



export default function ProductDetailsNewDesign({
  productId,
  view = 'details',
  selectedVendor,
  vendorsList,
  updateVendor,
  featureFlags,
  selectedBusinessUnit,
  productType = PRODUCT_TYPE.REGULAR
}: Props) {
  const typeConfig = PRODUCT_TYPE_CONFIG[productType] ?? PRODUCT_TYPE_CONFIG[PRODUCT_TYPE.REGULAR];

  // Redirect if path includes /new-product and user lacks product creation permission
  const location = window.location.pathname;
  const { readOnly: readOnlyCreateDelete } = useReadPermission(typeConfig.permissions.createDelete);
  const isNewProductPath = location.includes(typeConfig.newPathSegment);
  const productListingUrl = typeConfig.listingUrl;
  useEffect(() => {
    if (isNewProductPath && readOnlyCreateDelete) {
      if (typeof navigate !== 'undefined') {
        navigate(productListingUrl);
      } else {
        window.location.href = productListingUrl;
      }
    }
  }, [isNewProductPath, readOnlyCreateDelete]);

  const [vendorAttributes, setVendorAttributes] = useState<Attribute[]>([]);
  const [shouldReloadVendorAttributes, setShouldReloadVendorAttributes] =
    useState(false);
  const [importProduct, setImportProduct] = useState<Product | null>(null);
  const [importedAttributeNames] = useState<
    Array<{ name: string; isSelectable: boolean }>
  >([]);
  const [attributesList, setAttributesList] = useState<Prod.ProductAttribute[]>(
    []
  );
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [warehouseConfirmationModalOpen, setWarehouseConfirmationModalOpen] = useState(false);
  const [publishedSKUList, setPublishedSKUList] = useState<string[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [variantsUploading, setVariantsUploading] = useState(false);
  const { token, user } = useAuth();
  // Fetch currencies using useCurrencies hook on mount
  const { countries } = useCountries();
  const { currencies } = useCurrencies();
  const [validation, setValidation] = useState(false);
  const [showProductVariantModal, setShowProductVariantModal] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  const [activeTabs, setActiveTabs] = useState<number | undefined>(0);
  const [productDetails, setProductDetail] = useState<Product>();
  const [productVariants, setProductVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingVariants, setLoadingVariants] = useState<boolean>(false);
  const [hasVariantRemoved, setHasVariantRemoved] = useState<boolean>(false);
  const [paginationObj, setPaginationObj] = useState<Pagination>();
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [skuForSearch, setSkuForSearch] = useState<string>('');
  const [priceType, setPriceType] = useState<string>('');
  const [sortByHeader, setSortByHeader] = useState<Sorting>();
  const [allVariantsSelection, setAllVariantsSelection] = React.useState(false);
  const [keepWarehouse, setKeepWarehouse] = React.useState(false);
  const [initialWarehouses, setInitialWarehouses] = React.useState<Warehouse[]>([]);
  const [isErrorHighlight, setIsErrorHighlight] = React.useState(false);
  const [columnSettings, setColumnSettings] = React.useState<any>();
  const [badges, setBadges] = React.useState<ProductBadge[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const { readOnly: readOnlyEditUpdate } = useReadPermission(typeConfig.permissions.editUpdate);
  const { readOnly: commissionReadOnly } = useReadPermission(PermissionCode.AFFILIATE_COMISSION_MANAGEMENT);
  const [smartVariantsHasPendingActions, setSmartVariantsHasPendingActions] = useState(false);
  const [showTabChangeConfirmation, setShowTabChangeConfirmation] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  // Owned here rather than inside AffiliateFormView because that view unmounts on every tab
  // switch: a form id typed but not yet applied — and any error against it — has to survive
  // navigating away and back, the same as an edited variant row. Cleared when a different
  // product is opened, which is what re-seeds it from that product's saved form ids.
  const [affiliateFormState, setAffiliateFormState] = useState<AffiliateFormState | null>(null);
  const affiliateFormError = hasAffiliateFormError(affiliateFormState);

  useEffect(() => {
    setAffiliateFormState(null);
  }, [productId]);

  // True while a recommendation-section editor is open; product Save/Submit are blocked until
  // it is saved or cancelled.
  const [recommendationEditorOpen, setRecommendationEditorOpen] = useState(false);

  useEffect(() => {
    setRecommendationEditorOpen(false);
  }, [productId]);

  const {
    productErrors,
    productErrorsCount,
    variantErrorsCount,
    variantErrors
  } = useMemo(() => {
    if (!productDetails || loading) {
      return {
        productErrors: [],
        productErrorsCount: 0,
        variantErrorsCount: 0
      };
    }

    // Only run the expensive validation when needed
    const productValidation = validateProduct(
      productDetails as Product,
      user?.role
    );

    // Calculate all values in a single pass
    return {
      productErrors: productValidation.productErrors,
      productErrorsCount: productValidation.productErrors.filter(
        ({ type }) => type === ProductErrorType.Error
      ).length,
      variantErrorsCount: productValidation.variantErrors.length,
      variantErrors: productValidation.variantErrors
    };
  }, [
    productDetails,
    loading,
    user?.role
  ]);

  const socket = useContext(SocketContext);
  // TODO: to remove feature flag from implementation.
  const draftReq = useAPI<Product>({
    undefined,
    isFeatureFlagEnabled: true,
    productType,
    promiseFn: Prod.loadLocalDraft,
  });

  const badgesReq = useAPI<ProductBadge[]>({
    deferFn: loadProductBadges,
    onResolve: (result: ProductBadge[]) => setBadges(result || []),
    onReject: () => setBadges([])
  });

  const columnSettingsReq = useAPI<UserConfiguration>({
    deferFn: getColumnSettings,
    onResolve: (result: any) => {
      setColumnSettings(result);
    },
    onReject: (err: any) => {
      console.log('err:', err);
    }
  });


  const updateColumnSettingsReq = useAPI<UserConfiguration>({
    deferFn: updateColumnSettings,
    onResolve: (result: any) => {
      setColumnSettings(result);
    },
    onReject: (err: any) => {
      console.log('err:', err);
    },
  });

  useEffect(() => {
    columnSettingsReq.run(USER_CONFIG_TYPES.VARIANT);
  }, [])

  useEffect(() => {
    badgesReq.run();
  }, [])

  const loadProducts = useAPI({
    deferFn: loadProductDetails,
    onResolve: (result: any) => {
      setProductDetail(result);
      updateVendor(result.vendor.name, result.brand?.id);
      setInitialWarehouses(result.warehouses)
      setLoading(false);
      socket.off(`product_${productId}`);
    },
    onReject: (err: any) => {
      socket.off(`product_${productId}`);
      setLoading(false);
      renderToast(toast.TYPE.ERROR, err.message);
    },
  });

  const getVariants = useAPI({
    deferFn: getVariantsByProductId,
    onResolve: (result: any) => {
      setLoadingVariants(false);
      setProductVariants(result.data);
      setPaginationObj(result.meta);
    },
    onReject: (err: any) => {
      console.log('err:', err);
      setLoadingVariants(false);
    },
  });

  const updateLoadProduct = () => {
    setLoading(true)
    setHasVariantRemoved(false);
    productId && loadProducts.run(productId);
  };

  const loadProductVariants = (resetPagination = false) => {

    const paginationObj = {
      currentPage: resetPagination ? 1 : currentPage,
      itemsPerPage,
      sortByHeader: resetPagination ? null : sortByHeader,
      skuForSearch: resetPagination ? '' : skuForSearch,
      priceType: resetPagination ? GENERAL_CONSTANTS.ALL : priceType
    };

    productId && getVariants.run(productId, paginationObj);
  };

  useEffect(() => {
    view !== TEXT_LABELS.NEW_PRODUCT && setLoading(true);
    view !== TEXT_LABELS.NEW_PRODUCT && updateLoadProduct();
  }, [productId]);

  useEffect(() => {
    view !== TEXT_LABELS.NEW_PRODUCT && setLoadingVariants(true);
    loadProductVariants();
    hasVariantRemoved && updateLoadProduct(); //due to delete case
    setSelectedVariants([]);
  }, [skuForSearch, itemsPerPage, currentPage, sortByHeader, priceType]);

  const producVariantStatusById = useAPI({
    deferFn: getProductById,
    onResolve: (result: any) => {
      result?.statusId === STATUS.PENDING
        ? setVariantsUploading(true)
        : setVariantsUploading(false);
    },
    onReject: (err: any) => {
      console.log('err:', err);
    },
  });

  const createAttributesReq = useAPI({
    deferFn: createAttributes,
    onResolve: (result) =>
      handleCreateAttributesResults(
        result as unknown as Promise<
          PromiseSettledResult<Prod.ProductAttribute>[]
        >
      ),
    onReject: (err) => {
      renderToast(toast.TYPE.ERROR, err.message);
    },
  });

  const handleCreateAttributesResults = async (
    results: Promise<PromiseSettledResult<Prod.ProductAttribute>[]>
  ) => {
    const settledResults = await results;
    let errorMessage = '';
    const correctAttributes = settledResults.reduce(
      (attributes: any, currentResult) => {
        if (currentResult.status === 'fulfilled') {
          return attributes.concat(currentResult.value);
        }
        errorMessage = currentResult.reason?.message;
        return attributes;
      },
      []
    );

    if (errorMessage.length) {
      renderToast(toast.TYPE.WARNING, attributeErrorMessage(errorMessage));
    }

    setAttributesList(correctAttributes);
  };

  let currentVendor: Vendor = selectedVendor;

  useEffect(() => {
    setValidation(false);
    if (
      productDetails &&
      (productDetails.status === TEXT_LABELS.PUBLISHED ||
        productDetails.originalId) &&
      publishedSKUList.length === 0
    ) {
      setPublishedSKUList(productVariants!.map((variant) => variant.sku));
    }
  }, [productDetails]);

  useEffect(() => {
    productDetails?.id && producVariantStatusById.run(productDetails?.id);
  }, [productDetails?.id]);

  useEffect(() => {
    // Use v2 API if business unit feature flag is enabled
    const useV2API = featureFlags?.businessUnit?.enabled;

    if (useV2API && productDetails?.brand?.businessUnit?.id) {
      getAttributesPerBusinessUnit(productDetails.brand.businessUnit?.id, token!)
        .then((attributes) => {
          setVendorAttributes(attributes);
          setShouldReloadVendorAttributes(false);
        })
        .catch((err) => {
          console.error('Error loading business unit attributes:', err);
          renderToast(toast.TYPE.ERROR, 'Failed to load attributes for business unit');
        });
    } else if (!useV2API && selectedVendor) {
      // Fallback to v1 vendor-scoped API
      getVendorAttributes(token!, selectedVendor.id).then((attributes) => {
        setVendorAttributes(attributes);
        setShouldReloadVendorAttributes(false);
      });
    }
  }, [selectedVendor, shouldReloadVendorAttributes, productDetails?.brand?.businessUnit?.id, featureFlags?.businessUnit?.enabled]);

  useEffect(() => {
    if (importedAttributeNames.length > 0) {
      createAttributesReq.run(importedAttributeNames, currentVendor.id);
    }
  }, [importedAttributeNames]);

  useEffect(() => {
    if (attributesList.length > 0 && importProduct) {
      const newImportProduct = importProduct;
      const modifiedImportedAttributeNames = importedAttributeNames.map(
        (atr) => {
          return atr.name;
        }
      );
      newImportProduct.variants.forEach((variant) => {
        const newVariantAttributes: VariantAttributeValue[] = [];
        variant.attributeValues.forEach((attributeValue, index) => {
          const validatedAttribute = attributesList.find(
            (attribute) =>
              modifiedImportedAttributeNames[index] === attribute.name
          );
          if (validatedAttribute) {
            newVariantAttributes.push({
              ...attributeValue,
              attributeId: validatedAttribute.id,
              isSelectable: attributeValue.isSelectable,
            });
          }
        });
        variant.attributeValues = newVariantAttributes;
      });

      // newImportProduct.attributes = Prod.classifyAttributes(newImportProduct, attributesList);
      newImportProduct.attributes = attributesList;
      newImportProduct.variants = newImportProduct.variants.map((variant) => {
        const newAttributeValues = variant.attributeValues.map((value) => {
          // const foundAttribute = newImportProduct.attributes.find(attribute => attribute.id === value.attributeId);

          return { ...value, isSelectable: value.isSelectable };
        });

        return { ...variant, attributeValues: newAttributeValues };
      });
      setImportProduct(importProduct);
    }
  }, [attributesList]);

  const previousLibraryPath: string =
    typeConfig.listingUrl === ROUTES.PRODUCTS ? (getStash(TEXT_LABELS.PRODUCTS) || ROUTES.PRODUCTS) : typeConfig.listingUrl;

  const reloadVendorAttributes = (value: boolean) => {
    setShouldReloadVendorAttributes(value);
  };



  const isWarehousesEdited = !compareWarehouseIds(productDetails?.warehouses.map(warehouse => warehouse.id) || [], initialWarehouses.map(warehouse => warehouse.id) || [])

  function update(product: Product) {
    draftReq.setData(product);
    setProductDetail(product);
  }

  function updateColumnSettingsFn(columnSettings: any) {
    updateColumnSettingsReq.run(columnSettings, USER_CONFIG_TYPES.VARIANT);
  }

  function validateDataForVariantTable(tabNum: number) {
    if (productDetails && !productDetails.brand) {
      renderToast(
        toast.TYPE.WARNING,
        PRODUCT_CONSTANTS.BRAND_SELECTION_WARNING
      );
      return false;
    }

    if (productDetails && !productDetails.id) {
      const smartVariantsTabEnabled = (productDetails?.status !== TEXT_LABELS.PUBLISHED || productDetails?.status !== TEXT_LABELS.READY) && isAIEnabled;
      const toastMessage = smartVariantsTabEnabled ? `${PRODUCT_CONSTANTS.SAVE_WARNING}/smart variants.` : PRODUCT_CONSTANTS.SAVE_WARNING;
      console.log('toastMessage', smartVariantsTabEnabled, toastMessage);
      renderToast(toast.TYPE.WARNING, toastMessage);
      return false;
    }

    // Resolved by key, not index — the rendered tab list differs per product type.
    const targetKey = productTabs[tabNum]?.tabKey;
    const currentKey = productTabs[activeTabs ?? 0]?.tabKey;

    // Check if user is trying to leave Smart Variants tab with pending actions
    // Skip this check if we're already navigating after user confirmation
    if (smartVariantsHasPendingActions &&
      currentKey === TAB_KEYS.SMART_VARIANTS &&
      targetKey !== TAB_KEYS.SMART_VARIANTS) {
      setPendingTabChange(tabNum);
      setShowTabChangeConfirmation(true);
      return false;
    }

    if (isWarehousesEdited && targetKey === TAB_KEYS.VARIANTS) {
      setWarehouseConfirmationModalOpen(true)
      return false;
    }

    if (saveDisabled && SAVE_REQUIRED_TAB_KEYS.includes(targetKey)) {
      const toastId = TEXT_LABELS.SAVE_PRODUCT;
      renderToast(
        toast.TYPE.ERROR,
        TEXT_LABELS.SAVE_PRODUCT_ERROR,
        {
          toastId,
          autoClose: false,
        }
      );
      setIsErrorHighlight(true)
      return false
    }

    return true;
  }

  let routeView = null;
  const importExcelButton = (
    <Button
      margin="0 4px 0 10px"
      onClick={() => setShowProductVariantModal(true)}
      bgColor={Background.navy}
      borderColor={Background.navy}
      width='123px'
      disabled={(readOnlyEditUpdate && !!productDetails?.id) || productDetails?.status !== TEXT_LABELS.DRAFT}
      {...productDetails?.status === PRODUCT_STATUS.DRAFT ? { permissionCode: typeConfig.permissions.importXlsx } : {}}
    >
      {TEXT_LABELS.IMPORT_XLSX}
    </Button>
  );


  const keepWarehouses = () => {
    setKeepWarehouse(true)
    setWarehouseConfirmationModalOpen(false)
  }

  const discardWarehouses = () => {
    setKeepWarehouse(false)
    updateLoadProduct()
    setWarehouseConfirmationModalOpen(false)
    if (!initialWarehouses.length) {
      renderToast(
        toast.TYPE.WARNING,
        PRODUCT_CONSTANTS.WAREHOUSE_SELECTION_WARNING_TOAST
      );
    } else {
      navigate(`${detailsUrl}/variants`)
    }
  }

  const keepSmartVariants = () => {
    setShowTabChangeConfirmation(false)
  }

  const discardSmartVariants = () => {
    setSmartVariantsHasPendingActions(false);
    setShowTabChangeConfirmation(false);

    if (pendingTabChange !== null) {
      setSmartVariantsHasPendingActions(false);

      setShowTabChangeConfirmation(false);
      const targetTab = productTabs[pendingTabChange];
      if (targetTab && targetTab.link) {
        navigate(targetTab.link);
      }
      // Reset the flag after a short delay to allow navigation to complete
      setTimeout(() => {
        setShowTabChangeConfirmation(false);
        setPendingTabChange(null);
      }, 100);
    }
  }

  const errorKeys = useMemo(() =>
    productErrors.filter(error => error.type === PRODUCT_CONSTANTS.ERROR)
      .map(error => error.key),
    [productErrors]
  );

  const commonProps = useMemo(() => ({
    featureFlags,
    loading: loading,
    product: productDetails!,
    update,
    selectedVendor: currentVendor,
    disabled: (readOnlyEditUpdate && !!productDetails?.id) || productDetails?.status !== TEXT_LABELS.DRAFT || variantsUploading,
    stockFlag: productDetails?.status !== TEXT_LABELS.PUBLISHED,
    user,
    selectedBusinessUnit,
    commissionReadOnly,
    badges,
  }), [
    featureFlags,
    loading,
    productDetails,
    currentVendor,
    variantsUploading,
    selectedBusinessUnit,
    commissionReadOnly,
    badges,
  ]);

  const handleItemsPerPageChange = useCallback((value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  const handleCurrentPageChange = useCallback((value: number) =>
    setCurrentPage(value),
    []
  );

  const isAIEnabled = typeConfig.aiEnabled && selectedVendor?.configuration?.aiEnabled && user && grantAccess(featureFlags, FeatureFlagValues.AI_VARIANT_GENERATION, user);

  const customPagination = useMemo(() => ({
    itemsPerPage,
    setItemsPerPage: handleItemsPerPageChange,
    currentPage,
    setCurrentPage: handleCurrentPageChange,
    totalPages: paginationObj?.totalPages,
    baseRoute: ROUTES.PRODUCT_DETAIL,
    shouldRedirect: false,
  }), [
    itemsPerPage,
    currentPage,
    paginationObj?.totalPages,
    handleItemsPerPageChange,
    handleCurrentPageChange
  ]);

  const renderView = useCallback((view: string) => {
    switch (view) {
      case TEXT_LABELS.C_PREVIEW:
        return (
          <PDPPreview
            loading={commonProps.loading}
            product={commonProps.product}
          />
        );

      case TEXT_LABELS.VARIANTS:
        return (
          <Suspense fallback={<LoadingIndicator />}>
            <MemoizedVariantTableV2
              {...commonProps}
              productVariants={productVariants}
              setProductVariants={setProductVariants}
              customPagination={customPagination}
              loadingVariants={loadingVariants}
              setSkuForSearch={setSkuForSearch}
              setPriceType={setPriceType}
              skuForSearch={skuForSearch}
              sortByHeader={sortByHeader}
              setSortByHeader={setSortByHeader}
              vendorAttributes={vendorAttributes}
              publishedSKUList={publishedSKUList}
              reloadVendorAttributes={reloadVendorAttributes}
              currencies={currencies}
              role={user?.role}
              selectedVariants={selectedVariants}
              setSelectedVariants={setSelectedVariants}
              importExcelButton={importExcelButton}
              setHasVariantRemoved={setHasVariantRemoved}
              countries={countries}
              allVariantsSelection={allVariantsSelection}
              setAllVariantsSelection={setAllVariantsSelection}
              columnSettingsData={columnSettings}
              updateColumnSettings={updateColumnSettingsFn}
              variantErrors={variantErrors}
              loadProductVariants={loadProductVariants}
              loadProducts={updateLoadProduct}
            />
          </Suspense>
        );

      case TEXT_LABELS.DETAILS:
        return (
          <ProductEditorV2
            {...commonProps}
            setSaveDisabled={setSaveDisabled}
            currencies={currencies}
            validation={validation}
            errorKeys={errorKeys}
            isErrorHighlight={isErrorHighlight}
            setProductVariants={setProductVariants}
            productVariants={productVariants}
            selectedBusinessUnit={selectedBusinessUnit}
            countries={countries}
            onRecommendationEditorChange={setRecommendationEditorOpen}
          />
        );

      case TEXT_LABELS.NEW_PRODUCT:
        return (
          <ProductEditorV2
            {...commonProps}
            product={draftReq.data!}
            setSaveDisabled={setSaveDisabled}
            currencies={currencies}
            validation={validation}
            errorKeys={errorKeys}
            isErrorHighlight={isErrorHighlight}
            setProductVariants={setProductVariants}
            selectedBusinessUnit={selectedBusinessUnit}
            countries={countries}
            onRecommendationEditorChange={setRecommendationEditorOpen}
          />
        );

      case TEXT_LABELS.WAREHOUSES:
        return (
          <Suspense fallback={<LoadingIndicator />}>
            <MemoizedVariantTableV2
              {...commonProps}
              productVariants={productVariants}
              setProductVariants={setProductVariants}
              customPagination={customPagination}
              loadingVariants={loadingVariants}
              setSkuForSearch={setSkuForSearch}
              sortByHeader={sortByHeader}
              setSortByHeader={setSortByHeader}
              vendorAttributes={vendorAttributes}
              publishedSKUList={publishedSKUList}
              reloadVendorAttributes={reloadVendorAttributes}
              currencies={currencies}
              role={user?.role}
              selectedVariants={selectedVariants}
              setSelectedVariants={setSelectedVariants}
              importExcelButton={importExcelButton}
              setHasVariantRemoved={setHasVariantRemoved}
              countries={countries}
              allVariantsSelection={allVariantsSelection}
              setAllVariantsSelection={setAllVariantsSelection}
              skuForSearch={skuForSearch}
              warehouseMode
              columnSettingsData={columnSettings}
              updateColumnSettings={updateColumnSettingsFn}
              variantErrors={variantErrors}
              loadProductVariants={loadProductVariants}
              loadProducts={updateLoadProduct}
            />
          </Suspense>
        );

      case TEXT_LABELS.AFFILIATE_FORM:
        return (
          <AffiliateFormView
            product={commonProps.product}
            update={commonProps.update}
            countries={countries}
            disabled={commonProps.disabled}
            formState={affiliateFormState}
            onFormStateChange={setAffiliateFormState}
          />
        );

      case TEXT_LABELS.SMART_VARIANTS:
        return (
          <Suspense fallback={<LoadingIndicator />}>
            <SmartVariantsView
              {...commonProps}
              productId={productId}
              selectedVendor={selectedVendor}
              featureFlags={featureFlags}
              productVariants={productVariants}
              loadProducts={updateLoadProduct}
              variantErrors={variantErrors}
              onActionButtonsStateChange={setSmartVariantsHasPendingActions}
              loadProductVariants={loadProductVariants}
              vendorAttributes={vendorAttributes}
              reloadVendorAttributes={reloadVendorAttributes}
              customPagination={customPagination}
              isAIEnabled={isAIEnabled}
            />
          </Suspense>
        );
      default:
        return null;
    }
  }, [
    commonProps,
    productVariants,
    loadingVariants,
    customPagination,
    skuForSearch,
    sortByHeader,
    vendorAttributes,
    publishedSKUList,
    currencies,
    user?.role,
    selectedVariants,
    importExcelButton,
    countries,
    allVariantsSelection,
    columnSettings,
    validation,
    errorKeys,
    isErrorHighlight,
    draftReq.data,
    affiliateFormState,
  ]);

  if (productDetails && currentVendor) {
    routeView = renderView(view);
  }

  if (draftReq.data && view === TEXT_LABELS.NEW_PRODUCT && currentVendor) {
    routeView = renderView(view);
  }


  const detailsUrl = productId
    ? `${typeConfig.detailUrl}${productId}`
    : typeConfig.newUrl;

  const productTabs = useMemo(
    () => productDetailsTab(detailsUrl, productDetails, isWarehousesEdited, saveDisabled, isAIEnabled),
    [detailsUrl, productDetails, isWarehousesEdited, saveDisabled, isAIEnabled]
  );

  // TAB_KEYS values match the route `view` segments, so the active tab is just the position
  // of the matching key in the rendered list. Views without a tab (preview, newProduct)
  // fall back to Details.
  useEffect(() => {
    const index = productTabs.findIndex((tab: any) => tab && tab.tabKey === view);
    setActiveTabs(index >= 0 ? index : 0);
  }, [view, productTabs]);

  // Redirect to details page if accessing warehouses/variants with missing critical fields
  useEffect(() => {
    // Only check after product is loaded
    if (loading) return;

    // Only redirect if on warehouses or variants tab
    const shouldRedirect = (view === TEXT_LABELS.WAREHOUSES || view === TEXT_LABELS.VARIANTS);

    // Check if there are critical product-level errors
    if (shouldRedirect && productErrorsCount > 0) {
      navigate(detailsUrl);
    }
  }, [loading, view, productErrorsCount, detailsUrl]);

  const uploadVariantModal = (
    <UploadProductVariant
      show={showProductVariantModal}
      onClose={() => setShowProductVariantModal(false)}
      product={productDetails!}
      role={user?.role}
      variantsUploading={variantsUploading}
      setVariantsUploading={setVariantsUploading}
      loadProducts={updateLoadProduct}
      loadProductVariants={loadProductVariants}
      featureFlags={featureFlags}
      isValidating={isValidating}
      setIsValidating={setIsValidating}
    />
  );

  // warehouse tab error icon will display on these conditions
  let warehouseErrorCount = 0;
  const { hasVariantsHavePurchaseType, hasVariantsHaveRentType } = useMemo(() => {
    let hasPurchase = false;
    let hasRent = false;

    if (!productDetails?.variants) {
      return { hasVariantsHavePurchaseType: false, hasVariantsHaveRentType: false };
    }

    // Single-pass optimization
    for (let i = 0; i < productDetails.variants.length; i++) {
      const variant = productDetails.variants[i];
      if (variant.purchase) hasPurchase = true;
      if (variant.rent) hasRent = true;

      // Early termination - stop when we've found both types
      if (hasPurchase && hasRent) break;
    }

    return {
      hasVariantsHavePurchaseType: hasPurchase,
      hasVariantsHaveRentType: hasRent
    };
  }, [productDetails?.variants]);

  const purchaseWarehouseAdded = productDetails?.warehouses.some((warehouse) => warehouse.type === TEXT_LABELS.PURCHASE_C);
  const rentWarehouseAdded = productDetails?.warehouses.some((warehouse) => warehouse.type === TEXT_LABELS.RENT_C);
  if ((hasVariantsHavePurchaseType && hasVariantsHaveRentType) && (!purchaseWarehouseAdded && !rentWarehouseAdded)) {
    warehouseErrorCount = 2
  } else if (hasVariantsHavePurchaseType && !purchaseWarehouseAdded) {
    warehouseErrorCount = 1
  } else if (hasVariantsHaveRentType && !rentWarehouseAdded) {
    warehouseErrorCount = 1
  }


  return (
    <Page>
      <FlexboxContainer
        justifyContent="center"
        width="100%"
        alignItems="center"
      >
        <FlexboxContainerComponent width="100%" flexDirection="column">
          <FlexboxContainerComponent
            width="100%"
            margin="30px 0 0 0"
            flexDirection="row"
            justifyContent="space-between"
          >
            <TabsBarComponent
              height="auto"
              tabs={productTabs}
              activeTab={activeTabs}
              theme="brandDark"
              borderRadius="20px"
              margin="0 24px 8px 0"
              tabPanelPadding="0 !important"
              canGoToNextTab={validateDataForVariantTable}
              variantErrorsCount={variantErrorsCount}
              warehouseErrorCount={warehouseErrorCount}
              loading={loading}
            ></TabsBarComponent>
            <div className='tabs-border'></div>
            <FlexboxContainerComponent flexDirection="row">
              <HeaderButtonComponent
                loading={loading}
                setLoading={setLoading}
                detailsUrl={detailsUrl}
                saveDisabled={saveDisabled}
                setSaveDisabled={setSaveDisabled}
                productDetails={productDetails}
                currentVendor={currentVendor}
                token={token}
                currencies={currencies}
                variantErrorsCount={variantErrorsCount}
                productErrorsCount={productErrorsCount}
                affiliateFormError={affiliateFormError}
                recommendationEditorOpen={recommendationEditorOpen}
                warehouseLength={productDetails?.warehouses.length}
                setIsSubmitModalOpen={setIsSubmitModalOpen}
                setValidation={setValidation}
                view={view}
                keepWarehouse={keepWarehouse}
                setKeepWarehouse={setKeepWarehouse}
                loadProducts={updateLoadProduct}
                loadProductVariants={loadProductVariants}
                disabled={(readOnlyEditUpdate && !!productDetails?.id) || productDetails?.status !== TEXT_LABELS.DRAFT}
                setIsErrorHighlight={setIsErrorHighlight}
                productType={productType}
                countries={countries}
              />
            </FlexboxContainerComponent>
          </FlexboxContainerComponent>
          {loading ? <LoadingIndicator /> : routeView}
        </FlexboxContainerComponent>
      </FlexboxContainer>
      {showProductVariantModal && uploadVariantModal}
      <SubmitModal
        setIsSubmitModalOpen={setIsSubmitModalOpen}
        isSubmitModalOpen={isSubmitModalOpen}
        productDetails={productDetails}
        currentVendor={currentVendor}
        previousLibraryPath={previousLibraryPath}
        token={token}
        currencies={currencies}
        user={user}
        socket={socket}
      />
      <KeepOrDiscardModal
        isSubmitModalOpen={warehouseConfirmationModalOpen || showTabChangeConfirmation}
        keep={warehouseConfirmationModalOpen ? keepWarehouses : keepSmartVariants}
        discard={warehouseConfirmationModalOpen ? discardWarehouses : discardSmartVariants}
        description={warehouseConfirmationModalOpen ? TEXT_LABELS.KEEP_OR_DISCARD : TEXT_LABELS.TAB_CHANGE_CONFIRMATION_MESSAGE}
      />
    </Page>
  );
}
